import type { RecurrenceOverride, RecurrenceRule, RuleInput, Task, TaskInput, TaskPatch } from "@app/core";
import { applyOverrides, buildRecurrenceRule, childrenOf, completeTask, computeRollup, createOverride, createTask, descendantIds, reorderedSiblingIds, snoozePatch, softDeleteTask, toRRuleString, updateTask as patchTask, wakeSnoozed, type SubtaskRollupMode } from "@app/core";
import { bulkUpsert } from "@app/storage";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { formatHmDate } from "../lib/format.js";
import { tr, trLocale } from "../lib/tr.js";
import { useCalendarsStore } from "./calendars.store.js";
import { recentCompletionOrigin, useCompletionCelebrationStore } from "./completionCelebration.store.js";
import { useRollupConfirmStore } from "./rollupConfirm.store.js";
import { useSettingsStore } from "./settings.store.js";
import { undoable } from "./undo.js";
import { appendOverride, upsertSingleOverride } from "./series/helpers.js";
import {
  detachOccurrence,
  type DetachPatch,
  editSeriesFromOccurrence as editSeriesOp,
  editSeriesUntil as editSeriesUntilOp,
  removeSeriesOccurrence as removeSeriesOp,
  rescheduleSeries as rescheduleSeriesOp,
  resizeSeries as resizeSeriesOp,
  resizeSeriesFromStart as resizeSeriesFromStartOp,
  type SeriesEditPatch,
} from "./series/index.js";

export interface AddInput extends TaskInput {
  recurrence?: RuleInput;
}

let tasksHydrateInflight: Promise<void> | null = null;

interface TasksState {
  loaded: boolean;
  tasks: Task[];
  rules: Record<string, RecurrenceRule>;
  overrides: Record<string, RecurrenceOverride[]>;
  hydrate: () => Promise<void>;
  add: (input: AddInput) => Promise<Task>;
  edit: (id: string, patch: TaskPatch, ruleInput?: RuleInput | null) => Promise<void>;
  complete: (id: string, done: boolean) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /** Soft-delete every task belonging to a calendar (used when the calendar is
   *  deleted). Returns how many were removed. */
  removeByCalendar: (calendarId: string) => Promise<number>;
  reschedule: (id: string, newDueAt: string) => Promise<void>;
  rescheduleSeries: (sourceId: string, oldOccurrence: Date, newDueAt: Date, tz?: string | null, rrule?: string) => Promise<void>;
  rescheduleOccurrence: (sourceId: string, oldOccurrence: string, newDueAt: string) => Promise<void>;
  resizeOccurrence: (sourceId: string, occurrence: string, newDurationMinutes: number) => Promise<void>;
  resizeSeries: (sourceId: string, occurrenceIso: string, newDurationMinutes: number) => Promise<void>;
  resizeSeriesFromTop: (sourceId: string, occurrenceIso: string, newDueAt: string, newDurationMinutes: number) => Promise<void>;
  detachWithPatch: (sourceId: string, occurrenceIso: string, patch: DetachPatch) => Promise<void>;
  removeSeriesOccurrence: (sourceId: string, occurrenceIso: string, mode: "single" | "following" | "all") => Promise<void>;
  editSeriesFromOccurrence: (sourceId: string, occurrenceIso: string, patch: SeriesEditPatch) => Promise<void>;
  editSeriesUntil: (ruleId: string, untilIso: string | null) => Promise<void>;
  completeOccurrence: (sourceId: string, occurrenceIso: string, done: boolean) => Promise<void>;
  /** Snooze a one-shot task until `atIso` (defer-the-nag: keeps due_at/calendar). */
  snooze: (id: string, atIso: string) => Promise<void>;
  /** Wake a snoozed task now (back to open). */
  unsnooze: (id: string) => Promise<void>;
  /** Snooze a single occurrence of a recurring series via a per-occurrence override. */
  snoozeOccurrence: (sourceId: string, occurrenceIso: string, atIso: string) => Promise<void>;
  /** Return any snoozed task/occurrence whose wake time has passed to "open".
   *  Called after hydrate + on the reminder tick. Persists only when due. */
  wakeDueSnoozes: () => Promise<void>;
  /** Re-parent `childId` under `parentId` and clear its due_at (re-nest it inside
   *  the parent's calendar block). The inverse of dragging a subtask out. */
  nestUnder: (childId: string, parentId: string) => Promise<void>;
  /** Reorder `draggedId` to `targetId`'s slot among their shared parent's
   *  subtasks, persisting fresh sequential `subtask_order` values. */
  reorderSubtask: (parentId: string, draggedId: string, targetId: string) => Promise<void>;
  duplicate: (task: Task) => Promise<Task>;
  setColor: (id: string, color: string | null) => Promise<void>;
  setPriority: (id: string, priority: Task["priority"]) => Promise<void>;
  loadRule: (ruleId: string) => Promise<RecurrenceRule | null>;
}

export const useTasksStore = create<TasksState>((set, get) => ({
  loaded: false,
  tasks: [],
  rules: {},
  overrides: {},

  hydrate() {
    if (tasksHydrateInflight) return tasksHydrateInflight;
    tasksHydrateInflight = hydrateTasksOnce(set)
      .then(() => get().wakeDueSnoozes())
      .finally(() => { tasksHydrateInflight = null; });
    return tasksHydrateInflight;
  },

  async add(input) {
    return undoable("Add task", tr("toast.task.added"), async () => {
      const { tasks, recurrence } = getServices();
      // Every task MUST belong to a calendar. The form seeds calendar_id from the
      // default, but other creation paths (month/all-day drag-create, AI quick-add)
      // don't — so backfill it here from the default (or any existing) calendar.
      // This is the single chokepoint all UI creation flows through.
      const withCalendar: AddInput =
        input.calendar_id == null ? { ...input, calendar_id: resolveDefaultCalendarId() } : input;
      input = withCalendar;
      let recurrenceId: string | null = input.recurrence_id ?? null;
      if (input.recurrence && input.due_at) {
        // The task's tz is authoritative for the series — keep the rule in sync.
        const rule = { ...buildRecurrenceRule(input.recurrence, new Date(input.due_at)), tz: input.tz ?? null };
        await recurrence.insert(rule);
        recurrenceId = rule.id;
        set({ rules: { ...get().rules, [rule.id]: rule } });
      }
      const t = createTask({ ...input, recurrence_id: recurrenceId });
      await tasks.insert(t);
      set({ tasks: [...get().tasks, t] });
      return t;
    });
  },

  async edit(id, patch, ruleInput) {
    const { tasks, recurrence, recurrenceOverrides } = getServices();
    const current = get().tasks.find((t) => t.id === id);
    if (!current) return;
    await undoable("Edit task", tr("toast.task.updated"), async () => {
    let nextPatch = patch;
    if (ruleInput !== undefined) {
      if (ruleInput && (patch.due_at ?? current.due_at)) {
        const dt = new Date((patch.due_at ?? current.due_at) as string);
        const newRrule = toRRuleString(ruleInput);
        const newDtstart = dt.toISOString();
        if (current.recurrence_id) {
          const oldRule = get().rules[current.recurrence_id] ?? await recurrence.findById(current.recurrence_id);
          if (oldRule) {
            if (oldRule.rrule !== newRrule || oldRule.dtstart !== newDtstart) {
              const delta = new Date(newDtstart).getTime() - new Date(oldRule.dtstart).getTime();
              // "all" edits translate the whole series by the drag delta. The end
              // boundary (until) is just another time anchor, so it shifts with
              // dtstart — otherwise moving a (capped) series past its end date strands
              // dtstart beyond until and the series expands to nothing (disappears).
              const shiftedUntil = oldRule.until && delta !== 0
                ? new Date(new Date(oldRule.until).getTime() + delta).toISOString()
                : oldRule.until;
              const updated: RecurrenceRule = { ...oldRule, rrule: newRrule, dtstart: newDtstart, until: shiftedUntil, updated_at: new Date().toISOString() };
              await recurrence.update(updated);
              set({ rules: { ...get().rules, [updated.id]: updated } });
              // When dtstart shifts, migrate all override refs by the same delta so
              // existing cancellations and overrides continue to match shifted occurrences.
              if (oldRule.dtstart !== newDtstart) {
                const existing = get().overrides[oldRule.id] ?? [];
                if (existing.length > 0) {
                  const now = new Date().toISOString();
                  const migrated = existing.map((o) => ({
                    ...o,
                    recurrence_id_ref: new Date(new Date(o.recurrence_id_ref).getTime() + delta).toISOString(),
                    due_at: o.due_at ? new Date(new Date(o.due_at).getTime() + delta).toISOString() : o.due_at,
                    updated_at: now,
                  }));
                  for (const o of migrated) await recurrenceOverrides.update(o);
                  const sorted = migrated.slice().sort((a, b) => a.recurrence_id_ref.localeCompare(b.recurrence_id_ref));
                  set({ overrides: { ...get().overrides, [oldRule.id]: sorted } });
                }
              }
            }
          } else {
            // Rule row missing (corrupt link) — recreate it, keeping tz in sync
            // like the fresh-rule branch below.
            const rule = { ...buildRecurrenceRule(ruleInput, dt), tz: (patch.tz !== undefined ? patch.tz : current.tz) ?? null };
            await recurrence.insert(rule);
            set({ rules: { ...get().rules, [rule.id]: rule } });
            nextPatch = { ...patch, recurrence_id: rule.id };
          }
        } else {
          const rule = { ...buildRecurrenceRule(ruleInput, dt), tz: (patch.tz !== undefined ? patch.tz : current.tz) ?? null };
          await recurrence.insert(rule);
          set({ rules: { ...get().rules, [rule.id]: rule } });
          nextPatch = { ...patch, recurrence_id: rule.id };
        }
      } else {
        nextPatch = { ...patch, recurrence_id: null };
      }
    }
    // tz is a whole-series property — keep the rule in sync with the base task.
    const ruleId = nextPatch.recurrence_id ?? current.recurrence_id;
    if (patch.tz !== undefined && ruleId) {
      const r = get().rules[ruleId] ?? await recurrence.findById(ruleId);
      if (r && r.tz !== (patch.tz ?? null)) {
        const ur: RecurrenceRule = { ...r, tz: patch.tz ?? null, updated_at: new Date().toISOString() };
        await recurrence.update(ur);
        set({ rules: { ...get().rules, [ur.id]: ur } });
      }
    }
    const next = patchTask(current, nextPatch);
    // Optimistic: repaint immediately, then persist. The desktop SQLite write is
    // an IPC hop; blocking the UI on it is what made drags/color-changes feel
    // laggy. On a write failure, roll the task list back and rethrow.
    const prevTasks = get().tasks;
    set({ tasks: prevTasks.map((t) => (t.id === id ? next : t)) });
    try {
      await tasks.update(next);
    } catch (e) {
      set({ tasks: prevTasks });
      throw e;
    }
    });
  },

  async complete(id, done) {
    const celebrationOrigin = done ? recentCompletionOrigin() : null;
    const { tasks } = getServices();
    const current = get().tasks.find((t) => t.id === id);
    if (!current) return;
    const { completed } = completeTask(current, null);
    const next = done ? completed : patchTask(current, { status: "open", completed_at: null });

    // State reflecting the target's new status — roll-up math reads from this.
    let nextTasks = get().tasks.map((t) => (t.id === id ? next : t));
    const mode = await resolveRollupMode(current, done, nextTasks);
    const eff = mode === "abort"
      ? { complete: [], reopen: [] }
      : computeRollup(nextTasks, id, done, mode);
    if (mode === "abort") return; // user cancelled the popup — leave everything untouched

    const ts = new Date().toISOString();
    const changed: Task[] = [next];
    for (const cid of eff.complete) {
      const t = nextTasks.find((x) => x.id === cid);
      if (t && t.status !== "done") changed.push(patchTask(t, { status: "done", completed_at: ts }, () => new Date(ts)));
    }
    for (const cid of eff.reopen) {
      const t = nextTasks.find((x) => x.id === cid);
      if (t && t.status === "done") changed.push(patchTask(t, { status: "open", completed_at: null }, () => new Date(ts)));
    }
    await undoable(done ? "Complete task" : "Reopen task", tr(done ? "toast.task.completed" : "toast.task.reopened"), async () => {
      for (const c of changed) await tasks.update(c);
      const byId = new Map(changed.map((c) => [c.id, c]));
      set({ tasks: nextTasks.map((t) => byId.get(t.id) ?? t) });
    });
    if (done && current.status !== "done") useCompletionCelebrationStore.getState().launch(celebrationOrigin);
  },

  async remove(id) {
    const current = get().tasks.find((t) => t.id === id);
    if (!current) return;
    await undoable("Delete task", tr("toast.task.deleted"), async () => {
      // Cascade soft-delete: a parent takes its whole subtask subtree with it.
      // One native batch — a big subtree isn't one IPC write per descendant.
      const victims = [current, ...descendantIds(get().tasks, id)
        .map((d) => get().tasks.find((t) => t.id === d))
        .filter((t): t is NonNullable<typeof t> => Boolean(t))];
      await bulkUpsert(getServices().db, { tasks: victims.map((v) => softDeleteTask(v)) });
      const gone = new Set(victims.map((v) => v.id));
      set({ tasks: get().tasks.filter((t) => !gone.has(t.id)) });
    });
  },

  async removeByCalendar(calendarId) {
    // NOT undoable: this runs as part of the calendar-delete cascade in
    // calendars.store. Snapshotting just the tasks here would let "Undo" restore
    // them while the calendar stays deleted (a desynced state), so it's excluded.
    const victims = get().tasks.filter((t) => t.calendar_id === calendarId);
    await bulkUpsert(getServices().db, { tasks: victims.map((t) => softDeleteTask(t)) });
    if (victims.length) {
      const ids = new Set(victims.map((t) => t.id));
      set({ tasks: get().tasks.filter((t) => !ids.has(t.id)) });
    }
    return victims.length;
  },

  // reschedule / nestUnder / setColor delegate to the (already-undoable) edit,
  // so they inherit its snapshot + Undo toast — no separate wrapping needed.
  async reschedule(id, newDueAt) { await get().edit(id, { due_at: newDueAt }); },

  async rescheduleSeries(sourceId, oldOccurrence, newDueAt, tz, rrule) { await undoable("Reschedule series", tr("toast.task.seriesRescheduled"), () => rescheduleSeriesOp(set, get, sourceId, oldOccurrence, newDueAt, tz, rrule)); },
  async rescheduleOccurrence(sourceId, oldOccurrenceIso, newDueAtIso) { await undoable("Reschedule occurrence", tr("toast.task.occurrenceRescheduled"), () => detachOccurrence(set, get, sourceId, oldOccurrenceIso, { due_at: newDueAtIso })); },
  async resizeOccurrence(sourceId, occurrenceIso, newDurationMinutes) { await undoable("Resize occurrence", tr("toast.task.occurrenceResized"), () => detachOccurrence(set, get, sourceId, occurrenceIso, { due_at: occurrenceIso, duration_minutes: newDurationMinutes })); },
  async resizeSeries(sourceId, occurrenceIso, newDurationMinutes) { await undoable("Resize series", tr("toast.task.seriesResized"), () => resizeSeriesOp(set, get, sourceId, occurrenceIso, newDurationMinutes)); },
  async resizeSeriesFromTop(sourceId, occurrenceIso, newDueAt, newDurationMinutes) { await undoable("Resize series", tr("toast.task.seriesResized"), () => resizeSeriesFromStartOp(set, get, sourceId, occurrenceIso, newDueAt, newDurationMinutes)); },
  async detachWithPatch(sourceId, occurrenceIso, patch) { await undoable("Edit occurrence", tr("toast.task.occurrenceUpdated"), () => detachOccurrence(set, get, sourceId, occurrenceIso, patch)); },
  async removeSeriesOccurrence(sourceId, occurrenceIso, mode) { await undoable("Delete from series", tr("toast.task.deletedFromSeries"), () => removeSeriesOp(set, get, sourceId, occurrenceIso, mode)); },
  async editSeriesFromOccurrence(sourceId, occurrenceIso, patch) { await undoable("Edit series", tr("toast.task.seriesUpdated"), () => editSeriesOp(set, get, sourceId, occurrenceIso, patch)); },
  async editSeriesUntil(ruleId, untilIso) { await undoable("Change series end", tr("toast.task.seriesEndChanged"), () => editSeriesUntilOp(set, get, ruleId, untilIso)); },

  async completeOccurrence(sourceId, occurrenceIso, done) {
    const celebrationOrigin = done ? recentCompletionOrigin() : null;
    const source = get().tasks.find((t) => t.id === sourceId);
    if (!source || !source.recurrence_id) return;
    const wasDone = applyOverrides(
      source,
      occurrenceIso,
      get().overrides[source.recurrence_id] ?? [],
    )?.status === "done";
    await undoable(done ? "Complete occurrence" : "Reopen occurrence", tr(done ? "toast.task.occurrenceCompleted" : "toast.task.occurrenceReopened"), async () => {
      const override = createOverride({
        recurrence_id: source.recurrence_id!,
        recurrence_id_ref: occurrenceIso,
        range: "single",
        status: done ? "done" : "open",
      });
      await upsertSingleOverride(set, get, override);
    });
    if (done && !wasDone) useCompletionCelebrationStore.getState().launch(celebrationOrigin);
  },

  async snooze(id, atIso) {
    const { tasks } = getServices();
    const current = get().tasks.find((t) => t.id === id);
    if (!current) return;
    const next = patchTask(current, snoozePatch(atIso));
    await undoable("Snooze task", tr("toast.task.snoozedUntil", { when: fmtSnoozeUntil(atIso) }), async () => {
      const prev = get().tasks;
      set({ tasks: prev.map((t) => (t.id === id ? next : t)) });
      try { await tasks.update(next); }
      catch (e) { set({ tasks: prev }); throw e; }
    });
  },

  async unsnooze(id) {
    const { tasks } = getServices();
    const current = get().tasks.find((t) => t.id === id);
    if (!current) return;
    const next = patchTask(current, { status: "open", snoozed_until: null });
    await undoable("Wake task", tr("toast.task.woken"), async () => {
      const prev = get().tasks;
      set({ tasks: prev.map((t) => (t.id === id ? next : t)) });
      try { await tasks.update(next); }
      catch (e) { set({ tasks: prev }); throw e; }
    });
  },

  async snoozeOccurrence(sourceId, occurrenceIso, atIso) {
    const source = get().tasks.find((t) => t.id === sourceId);
    if (!source || !source.recurrence_id) return;
    await undoable("Snooze occurrence", tr("toast.task.snoozedUntil", { when: fmtSnoozeUntil(atIso) }), async () => {
      const override = createOverride({
        recurrence_id: source.recurrence_id!,
        recurrence_id_ref: occurrenceIso,
        range: "single",
        status: "snoozed",
        snoozed_until: atIso,
      });
      await upsertSingleOverride(set, get, override);
    });
  },

  async wakeDueSnoozes() {
    const { tasks, recurrenceOverrides } = getServices();
    const now = new Date();
    const nowMs = now.getTime();
    const ts = now.toISOString();

    const wokenTasks = wakeSnoozed(get().tasks, now);
    const wokenOverrides: RecurrenceOverride[] = [];
    for (const list of Object.values(get().overrides)) {
      for (const o of list) {
        if (o.status !== "snoozed") continue;
        if (o.snoozed_until && new Date(o.snoozed_until).getTime() > nowMs) continue;
        wokenOverrides.push({ ...o, status: "open", snoozed_until: null, updated_at: ts });
      }
    }
    if (wokenTasks.length === 0 && wokenOverrides.length === 0) return;

    for (const t of wokenTasks) await tasks.update(t);
    for (const o of wokenOverrides) await recurrenceOverrides.update(o);

    if (wokenTasks.length) {
      const byId = new Map(wokenTasks.map((t) => [t.id, t]));
      set({ tasks: get().tasks.map((t) => byId.get(t.id) ?? t) });
    }
    if (wokenOverrides.length) {
      const ovById = new Map(wokenOverrides.map((o) => [o.id, o]));
      const nextOv: Record<string, RecurrenceOverride[]> = {};
      for (const [k, list] of Object.entries(get().overrides)) nextOv[k] = list.map((o) => ovById.get(o.id) ?? o);
      set({ overrides: nextOv });
    }
  },

  async nestUnder(childId, parentId) {
    await get().edit(childId, { subtask_parent_id: parentId, due_at: null });
  },

  async reorderSubtask(parentId, draggedId, targetId) {
    const { tasks } = getServices();
    const ordered = reorderedSiblingIds(get().tasks, parentId, draggedId, targetId);
    const ts = new Date().toISOString();
    const changed: Task[] = [];
    ordered.forEach((id, i) => {
      const t = get().tasks.find((x) => x.id === id);
      if (t && t.subtask_order !== i) changed.push(patchTask(t, { subtask_order: i }, () => new Date(ts)));
    });
    if (changed.length === 0) return;
    await undoable("Reorder subtasks", tr("toast.task.subtasksReordered"), async () => {
      for (const c of changed) await tasks.update(c);
      const byId = new Map(changed.map((c) => [c.id, c]));
      set({ tasks: get().tasks.map((t) => byId.get(t.id) ?? t) });
    });
  },

  async duplicate(task) {
    const label = task.is_event ? "Duplicate event" : "Duplicate task";
    const done = tr(task.is_event ? "toast.task.eventDuplicated" : "toast.task.duplicated");
    return undoable(label, done, async () => {
      const snapshot = get().tasks; // stable source for the subtask tree
      const created: Task[] = [];

      // Deep-copy a task and its whole subtask subtree. The ROOT keeps its time
      // and becomes a standalone task (no parent); descendants are re-parented to
      // their fresh copy and have their specific time DROPPED (due_at = null) so
      // they nest inside the new parent block, as requested.
      const copySubtree = (src: Task, newParentId: string | null): Task => {
        const isRoot = newParentId === null;
        const copy = createTask({
          title: src.title,
          notes: src.notes,
          due_at: isRoot ? src.due_at : null,
          all_day: src.all_day,
          duration_minutes: src.duration_minutes,
          priority: src.priority,
          custom_color: src.custom_color,
          is_event: src.is_event,
          location: src.location,
          label_ids: [...src.label_ids],
          reminder_offsets: [...src.reminder_offsets],
          tz: src.tz,
          topic_ids: [...src.topic_ids],
          calendar_id: src.calendar_id ?? resolveDefaultCalendarId(),
          ...(isRoot ? {} : { subtask_parent_id: newParentId, subtask_order: src.subtask_order }),
          // Intentionally omitted: recurrence_id, deadline_at, external_id,
          // ical_uid, etag, source, external_calendar_id (+ root's parent link).
        });
        created.push(copy);
        for (const child of childrenOf(snapshot, src.id)) copySubtree(child, copy.id);
        return copy;
      };

      const root = copySubtree(task, null);
      await bulkUpsert(getServices().db, { tasks: created });
      set({ tasks: [...get().tasks, ...created] });
      return root;
    });
  },

  async setColor(id, color) {
    const { tasks } = getServices();
    const current = get().tasks.find((t) => t.id === id);
    if (!current || current.custom_color === color) return;
    await undoable("Edit task", tr("toast.task.updated"), async () => {
      const next = patchTask(current, { custom_color: color });
      const prevTasks = get().tasks;
      set({ tasks: prevTasks.map((t) => (t.id === id ? next : t)) });
      try {
        await tasks.update(next);
      } catch (e) {
        set({ tasks: prevTasks });
        throw e;
      }
    });
  },
  async setPriority(id, priority) {
    const { tasks } = getServices();
    const current = get().tasks.find((t) => t.id === id);
    if (!current || current.priority === priority) return;
    await undoable("Edit task", tr("toast.task.updated"), async () => {
      const next = patchTask(current, { priority });
      const prevTasks = get().tasks;
      set({ tasks: prevTasks.map((t) => (t.id === id ? next : t)) });
      try {
        await tasks.update(next);
      } catch (e) {
        set({ tasks: prevTasks });
        throw e;
      }
    });
  },

  async loadRule(ruleId) {
    const cached = get().rules[ruleId];
    if (cached) return cached;
    const { recurrence } = getServices();
    const rule = await recurrence.findById(ruleId);
    if (rule) set({ rules: { ...get().rules, [rule.id]: rule } });
    return rule;
  },
}));

/** Short human label for a snooze wake instant, used in the Undo toast. Same-day
 *  shows just the time; otherwise prepends the weekday. */
/** Wake time for the snooze toast. Uses the APP's 12/24h setting and language
 *  (not the device locale) so it matches every other rendered time. */
function fmtSnoozeUntil(iso: string): string {
  const d = new Date(iso);
  const time = formatHmDate(d, useSettingsStore.getState().timeFormat === "12h");
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString(trLocale(), { weekday: "short" })} ${time}`;
}

async function hydrateTasksOnce(
  set: (partial: Partial<TasksState>) => void,
): Promise<void> {
  const { tasks, recurrence, recurrenceOverrides } = getServices();
  const all = await tasks.listActive();
  const ruleIds = new Set(all.map((t) => t.recurrence_id).filter((id): id is string => Boolean(id)));
  // Reload referenced rules FRESH from the repo (don't keep cached copies):
  // after undo/restore the same rule id may have reverted. Listing once avoids
  // an IPC/SQL round-trip per recurring series on startup.
  const rules: Record<string, RecurrenceRule> = {};
  for (const r of await recurrence.listActive()) {
    if (ruleIds.has(r.id)) rules[r.id] = r;
  }
  const allOverrides = await recurrenceOverrides.listAllActive();
  const overrides: Record<string, RecurrenceOverride[]> = {};
  for (const o of allOverrides) (overrides[o.recurrence_id] ??= []).push(o);
  for (const list of Object.values(overrides)) list.sort((a, b) => a.recurrence_id_ref.localeCompare(b.recurrence_id_ref));
  set({ tasks: all, rules, overrides, loaded: true });
}

/** Resolve the active roll-up mode for a completion. For the "ask" setting this
 *  pops the confirm popup (only when a cascade would actually change something),
 *  mapping the answer to a concrete core mode or "abort" (user cancelled). */
async function resolveRollupMode(
  current: Task,
  done: boolean,
  nextTasks: Task[],
): Promise<SubtaskRollupMode | "abort"> {
  const setting = useSettingsStore.getState().subtaskRollup;
  if (setting !== "ask") return setting;
  const wouldCascade = computeRollup(nextTasks, current.id, done, "two_way");
  if (wouldCascade.complete.length === 0 && wouldCascade.reopen.length === 0) return "none";
  const hasChildren = nextTasks.some((t) => t.subtask_parent_id === current.id);
  const choice = await useRollupConfirmStore.getState().ask({
    task: current, done, direction: hasChildren ? "down" : "up",
  });
  if (choice === "cancel") return "abort";
  return choice === "cascade" ? "two_way" : "none";
}

/** The calendar a new task should land in when the caller didn't specify one:
 *  the settings default if it still exists, else any existing calendar, else
 *  null (only before the calendars store has seeded its first-run "Personal"). */
function resolveDefaultCalendarId(): string | null {
  const calendars = useCalendarsStore.getState().calendars;
  const def = useSettingsStore.getState().defaultCalendarId;
  if (def && calendars.some((c) => c.id === def)) return def;
  return calendars[0]?.id ?? null;
}
